/**
 * A slot on the page that the model chat renders into instead of the rail.
 *
 * On the Overview there is nothing to consult the assistant BESIDE: the questions worth
 * asking about a model are the whole of what that tab is for, and its answers are prose,
 * tables and an executive summary, which are the things a 360px column reads worst. So
 * the Overview offers a slot and the chat takes the page width. Every other tab is work
 * with the assistant next to it, and there the rail is right.
 *
 * The chat is NOT re-mounted to move between them. `ModelChatPanel` is mounted once by
 * `ModelPage` for the whole model, holds the thread, the transcript and the open stream
 * in its own hooks, and only its portal target changes. Tabbing from Overview to the tree
 * therefore carries a conversation mid-answer: the DOM is re-attached in the rail, the
 * run keeps streaming into the same state, and the reader sees the same conversation in a
 * narrower frame. Mounting a second copy on the Overview instead would have been two
 * conversations wearing one name.
 *
 * A module-level subscription rather than context, for the same reason `askBus` is one:
 * the slot and the panel are on opposite sides of the router with no provider between
 * them, and the panel is mounted above the route that offers the slot.
 */
import React, { useCallback, useEffect, useState } from 'react';

type Listener = (host: HTMLElement | null) => void;

let host: HTMLElement | null = null;
const listeners = new Set<Listener>();

/**
 * One delivery, which cannot take anything else down with it.
 *
 * Used by BOTH the broadcast and the seeding, which is the point: a subscriber that
 * throws on its first value would otherwise throw out of `subscribeHeroHost` itself and
 * never get registered, so it would miss every LATER host as well. The guard being on
 * only one of the two paths is the shape a test caught here.
 *
 * Nothing useful can be done with the error either way: the worst case is a chat that
 * stays in the rail.
 */
function tell(listener: Listener, value: HTMLElement | null): void {
  try {
    listener(value);
  } catch {
    // deliberately ignored, see above
  }
}

function publish(next: HTMLElement | null): void {
  host = next;
  // A copy, so a subscriber added or removed during delivery does not change this pass.
  [...listeners].forEach((listener) => tell(listener, next));
}

/**
 * Watch the page slot. Returns the unsubscribe.
 *
 * The listener is told the CURRENT host immediately, not only on the next change. That
 * seeding is the whole contract: a panel mounting while the Overview is already on screen
 * would otherwise draw itself in the rail and stay there, because the slot published
 * before anything was listening.
 *
 * Separate from the hook so it can be exercised without a renderer, and so the hook has
 * no logic of its own to get wrong.
 */
export function subscribeHeroHost(listener: Listener): () => void {
  listeners.add(listener);
  tell(listener, host);
  return () => {
    listeners.delete(listener);
  };
}

/** The page slot, or null when the page on screen offers none. */
export function useHeroHost(): HTMLElement | null {
  const [current, setCurrent] = useState<HTMLElement | null>(host);
  useEffect(() => subscribeHeroHost(setCurrent), []);
  return current;
}

/**
 * The slot itself. Renders an empty div and hands it to whichever chat is listening.
 *
 * A callback ref, not `useEffect` over a `useRef`: the effect would run after the panel
 * has already rendered once with no host, and the panel's first paint is what decides
 * whether the shell reserves a rail column. The callback fires during commit, before
 * that is observable.
 *
 * Clearing on unmount is what returns the chat to the rail when the reader leaves this
 * tab, so it is not merely tidy: without it the panel would portal into a detached node
 * and the conversation would vanish from the page with nothing to click.
 */
export function ModelChatSlot(): JSX.Element {
  const attach = useCallback((element: HTMLDivElement | null) => {
    publish(element);
  }, []);
  return <div ref={attach} className="mcp-slot" />;
}

/** Publish a host directly. The slot does this itself; this is for tests. */
export function setHeroHost(element: HTMLElement | null): void {
  publish(element);
}
